import React, { useMemo, useState } from 'react';
import {
  Button,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  formatBakuMutuLine,
  isLikelyStrapiDocumentId,
  parseStoredParameterUjiDocumentIds,
  type StrapiBakuMutuMini,
  type StrapiSampleInformationRow,
} from '../../../../api/strapiCrm';
import { useAuth } from '../../../../auth/AuthContext';
import { useParameterUjisByDocumentIdsQuery } from '../../../../query/hooks';
import { ChemParameterUjiCell } from '../../../../components/ChemParameterUjiCell';
import type { DynamicEntityRow } from '../../../../api/strapiDynamicEntity';
import { jenisPengukuranMasterOptionLabel } from '../../../../components/JenisPengukuranMasterAutocomplete';
import type {
  QuotationFormGroupOtherCostSubtotal,
  QuotationFormGroupSampleSubtotal,
} from '../quotationFormGroups';
import type { QuotationFormOtherCostRow, QuotationFormSampleRow, QuotationFormValues } from '../quotationFormTypes';
import { quotationFormSampleLineSubtotal, QuotationFormBiayaLainnyaTabScreen } from './BiayaLainnya';
import { QuotationOtherCostFormDialog } from './OtherCostFormDialog';
import { QuotationSampleFormDialog } from './SampleFormDialog';

export type { QuotationFormSampleRow } from '../quotationFormTypes';
export { emptyQuotationFormSampleRow } from './SampleFormDialog';

export function quotationFormSamplesFromDetailLines(list: StrapiSampleInformationRow[]): QuotationFormSampleRow[] {
  return list.map((r, i) => {
    const jp = r.jenis_pengukuran;
    let jenisPengukuranMasterId = '';
    let jenisPengukuranLabel = '';
    if (jp && typeof jp === 'object' && 'documentId' in jp) {
      const o = jp as { documentId?: string; nama_jenis?: string | null; kode_jenis?: string | null };
      jenisPengukuranMasterId = String(o.documentId ?? '').trim();
      jenisPengukuranLabel = jenisPengukuranMasterOptionLabel(o as DynamicEntityRow);
    }
    const bm = r.baku_mutu;
    let bakuMutuMasterId = '';
    let bakuMutuLabel = '';
    if (bm && typeof bm === 'object' && typeof bm.documentId === 'string' && bm.documentId.trim()) {
      const o = bm as StrapiBakuMutuMini;
      bakuMutuMasterId = o.documentId.trim();
      bakuMutuLabel = formatBakuMutuLine(o) || bakuMutuMasterId;
    }
    return {
      key: r.documentId || `srv-${i}`,
      documentId: r.documentId,
      jenisPengukuranMasterId,
      jenisPengukuranLabel: jenisPengukuranLabel || undefined,
      parameterUji: r.parameter_uji ?? '',
      bakuMutuMasterId,
      bakuMutuLabel: bakuMutuLabel || undefined,
      jumlahTitik:
        r.sample_point_count != null && Number.isFinite(Number(r.sample_point_count))
          ? String(r.sample_point_count)
          : '1',
      harga: r.harga != null && Number.isFinite(Number(r.harga)) ? String(r.harga) : '',
    };
  });
}

function rowHasAnyField(row: QuotationFormSampleRow): boolean {
  return (
    Boolean(row.jenisPengukuranMasterId.trim()) ||
    Boolean(row.parameterUji.trim()) ||
    Boolean(row.bakuMutuMasterId.trim()) ||
    Boolean(row.harga.trim()) ||
    (Boolean(row.jumlahTitik.trim()) && row.jumlahTitik.trim() !== '1')
  );
}

export function quotationFormSampleRowInlineFieldErrors(row: QuotationFormSampleRow): {
  jumlahTitikInvalid: boolean;
  hargaInvalid: boolean;
} {
  const jt = Number.parseInt(row.jumlahTitik, 10);
  const jumlahTitikInvalid = row.jumlahTitik.trim() !== '' && (!Number.isFinite(jt) || jt < 1);
  const hTrim = row.harga.trim();
  const hNum = hTrim === '' ? null : Number(hTrim);
  const hargaInvalid = hTrim !== '' && !Number.isFinite(hNum);
  return { jumlahTitikInvalid, hargaInvalid };
}

function validateCompleteRow(
  row: QuotationFormSampleRow,
  indexLabel: number,
):
  | { valid: false; message: string }
  | {
      valid: true;
      jenis_pengukuran: string;
      parameter_uji: string;
      baku_mutu: string;
      sample_point_count: number;
      harga: number | null;
    } {
  const jenis_pengukuran = row.jenisPengukuranMasterId.trim();
  const parameter_uji = row.parameterUji.trim();
  const baku_mutu = row.bakuMutuMasterId.trim();
  if (!jenis_pengukuran || !parameter_uji || !baku_mutu) {
    return {
      valid: false,
      message: `Baris ${indexLabel}: pilih jenis pengukuran dan baku mutu dari master, lalu centang minimal satu parameter uji pada tabel.`,
    };
  }
  const jt = Number.parseInt(row.jumlahTitik, 10);
  if (!Number.isFinite(jt) || jt < 1) {
    return { valid: false, message: `Baris ${indexLabel}: jumlah titik minimal 1.` };
  }
  const hTrim = row.harga.trim();
  const hNum = hTrim === '' ? null : Number(hTrim);
  if (hTrim !== '' && !Number.isFinite(hNum)) {
    return { valid: false, message: `Baris ${indexLabel}: harga harus angka valid atau kosong.` };
  }
  return {
    valid: true,
    jenis_pengukuran,
    parameter_uji,
    baku_mutu,
    sample_point_count: jt,
    harga: hNum,
  };
}

export type QuotationSamplesSubmitValidatedLine = {
  kind: 'create';
  payload: {
    jenis_pengukuran: string;
    parameter_uji: string;
    baku_mutu: string;
    sample_point_count: number;
    harga: number | null;
  };
};

export type QuotationSamplesPersistedUpdateLine = {
  kind: 'update';
  documentId: string;
  payload: {
    jenis_pengukuran: string;
    parameter_uji: string;
    baku_mutu: string;
    sample_point_count: number;
    harga: number | null;
  };
};

export function validateQuotationFormSamplesForSave(rows: QuotationFormSampleRow[]):
  | { ok: false; message: string }
  | { ok: true; creates: QuotationSamplesSubmitValidatedLine[]; updates: QuotationSamplesPersistedUpdateLine[] } {
  if (rows.length === 0) {
    return { ok: false, message: 'Tambah minimal satu baris sampel (tombol Sampel baru).' };
  }

  const creates: QuotationSamplesSubmitValidatedLine[] = [];
  const updates: QuotationSamplesPersistedUpdateLine[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const label = i + 1;
    const { jumlahTitikInvalid, hargaInvalid } = quotationFormSampleRowInlineFieldErrors(row);
    if (jumlahTitikInvalid || hargaInvalid) {
      return {
        ok: false,
        message: `Baris ${label}: perbaiki format jumlah titik atau harga.`,
      };
    }

    if (row.documentId) {
      const v = validateCompleteRow(row, label);
      if (!v.valid) return { ok: false, message: v.message };
      updates.push({
        kind: 'update',
        documentId: row.documentId,
        payload: {
          jenis_pengukuran: v.jenis_pengukuran,
          parameter_uji: v.parameter_uji,
          baku_mutu: v.baku_mutu,
          sample_point_count: v.sample_point_count,
          harga: v.harga,
        },
      });
      continue;
    }

    if (!rowHasAnyField(row)) continue;
    const v = validateCompleteRow(row, label);
    if (!v.valid) return { ok: false, message: v.message };
    creates.push({
      kind: 'create',
      payload: {
        jenis_pengukuran: v.jenis_pengukuran,
        parameter_uji: v.parameter_uji,
        baku_mutu: v.baku_mutu,
        sample_point_count: v.sample_point_count,
        harga: v.harga,
      },
    });
  }

  if (creates.length === 0 && updates.length === 0) {
    return {
      ok: false,
      message: 'Minimal satu baris sampel harus diisi lengkap (jenis pengukuran, baku mutu, parameter uji, jumlah titik).',
    };
  }

  return { ok: true, creates, updates };
}

type Props = {
  disabled: boolean;
  /** Sembunyikan tombol tambah/ubah/hapus (mode lihat). */
  readOnly?: boolean;
  sampleRows: QuotationFormSampleRow[];
  onSampleRowsChange: (rows: QuotationFormSampleRow[]) => void;
  discount_amount: string;
  discount_is_percentage: boolean;
  discount_percentage: string;
  is_ppn: boolean;
  otherCostRows: QuotationFormOtherCostRow[];
  onFinancialPatch: (
    patch: Partial<
      Pick<
        QuotationFormValues,
        'discount_amount' | 'discount_is_percentage' | 'discount_percentage' | 'is_ppn' | 'otherCostRows'
      >
    >,
  ) => void;
  /** Sembunyikan ringkasan diskon/PPN/grand total (biaya lain tetap tampil). */
  hideFinancialSummary?: boolean;
  /** Subtotal sampel untuk ringkasan biaya (default: sampleRows). */
  financialSampleRows?: QuotationFormSampleRow[];
  /** Multiplier recurring per baris financial (index selaras financialSampleRows). */
  financialSampleRecurringCounts?: number[];
  /** Subtotal sampel per tab group (kontrak/konsultan). */
  groupSampleSubtotals?: QuotationFormGroupSampleSubtotal[];
  /** Subtotal biaya lain per group (ringkasan grand total grouped). */
  groupOtherCostSubtotals?: QuotationFormGroupOtherCostSubtotal[];
  /** Semua biaya lain untuk hitung grand total grouped. */
  allOtherCostRowsForGrandTotal?: QuotationFormOtherCostRow[];
  /** Multiplier jadwal berulang selaras allOtherCostRowsForGrandTotal. */
  allOtherCostRecurringCounts?: number[];
  /** Multiplier recurring untuk baris sampel di tab ini (kontrak group). */
  contractRecurringCount?: number;
};

type SampleDialogState = { mode: 'create' } | { mode: 'edit'; index: number };
type CostDialogState = { mode: 'create' } | { mode: 'edit'; index: number };

function formatIdr(n: number): string {
  return n.toLocaleString('id-ID', { maximumFractionDigits: 0 });
}

/** Tab Sampel: tabel baca-saja + modal tambah/ubah; biaya lain & total di bawah. */
export function QuotationFormSamplesTabScreen({
  disabled,
  readOnly = false,
  sampleRows,
  onSampleRowsChange,
  discount_amount,
  discount_is_percentage,
  discount_percentage,
  is_ppn,
  otherCostRows,
  onFinancialPatch,
  hideFinancialSummary = false,
  financialSampleRows,
  financialSampleRecurringCounts,
  groupSampleSubtotals,
  groupOtherCostSubtotals,
  allOtherCostRowsForGrandTotal,
  allOtherCostRecurringCounts,
  contractRecurringCount,
}: Props) {
  const { jwt } = useAuth();
  const [sampleDialog, setSampleDialog] = useState<SampleDialogState | null>(null);
  const [costDialog, setCostDialog] = useState<CostDialogState | null>(null);

  const allParameterUjiDocIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of sampleRows) {
      for (const t of parseStoredParameterUjiDocumentIds(r.parameterUji)) {
        if (isLikelyStrapiDocumentId(t)) set.add(t);
      }
    }
    return [...set];
  }, [sampleRows]);
  const parameterUjisLookupQ = useParameterUjisByDocumentIdsQuery(jwt, allParameterUjiDocIds);
  const parameterUjisMap = parameterUjisLookupQ.data ?? new Map();

  const sampleDialogInitial =
    sampleDialog?.mode === 'edit' ? (sampleRows[sampleDialog.index] ?? null) : null;

  const costDialogInitial =
    costDialog?.mode === 'edit' ? (otherCostRows[costDialog.index] ?? null) : null;

  const showSampleActions = !readOnly;
  const rowsForFinancial = financialSampleRows ?? sampleRows;
  const lineRecurringCount = contractRecurringCount ?? 1;

  return (
    <Stack spacing={2} sx={{ maxWidth: 1100 }}>
      {!readOnly ? (
        <Typography variant="body2" color="text.secondary">
          Daftar sampel dan biaya lain hanya ditampilkan di tabel; gunakan <strong>Sampel baru</strong> /{' '}
          <strong>Biaya lainnya baru</strong> untuk menambah, atau <strong>Ubah</strong> / <strong>Hapus</strong> pada
          setiap baris. Ringkasan total di bawah menghitung subtotal sampel
          {contractRecurringCount != null && contractRecurringCount > 1
            ? ` (jumlah titik × harga × ${contractRecurringCount} jadwal berulang)`
            : ' (jumlah titik × harga)'}
          , biaya lain, diskon, dan PPN.
        </Typography>
      ) : null}

      {showSampleActions ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={() => setSampleDialog({ mode: 'create' })}
            data-testid="quotation-form-new-sample"
          >
            Sampel baru
          </Button>
        </Stack>
      ) : null}

      <Table
        size="small"
        sx={{ '& .MuiTableCell-root': { verticalAlign: 'middle', py: 1 } }}
        data-testid="quotation-samples-table"
      >
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Jenis pengukuran</TableCell>
            <TableCell>Parameter uji</TableCell>
            <TableCell>Baku mutu</TableCell>
            <TableCell width={100}>Jumlah titik</TableCell>
            <TableCell width={120}>Harga</TableCell>
            <TableCell width={120} align="right">
              Subtotal
            </TableCell>
            {showSampleActions ? (
              <TableCell width={100} align="center">
                Aksi
              </TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {sampleRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showSampleActions ? 8 : 7}>
                <Typography variant="body2" color="text.secondary">
                  Belum ada sampel. Klik &quot;Sampel baru&quot; untuk menambah.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sampleRows.map((row, idx) => {
              const lineSub = quotationFormSampleLineSubtotal(row, lineRecurringCount);
              return (
                <TableRow key={row.key} data-testid={`quotation-sample-row-${idx}`}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.jenisPengukuranLabel?.trim() || row.jenisPengukuranMasterId || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="div">
                      <ChemParameterUjiCell raw={row.parameterUji} puLookup={parameterUjisMap} />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.bakuMutuLabel?.trim() || row.bakuMutuMasterId || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.jumlahTitik || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.harga.trim() ? formatIdr(Number.parseFloat(row.harga.replace(',', '.')) || 0) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {lineSub > 0 ? formatIdr(lineSub) : '—'}
                    </Typography>
                  </TableCell>
                  {showSampleActions ? (
                    <TableCell align="center">
                      <Tooltip title="Ubah">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Ubah sampel baris ${idx + 1}`}
                            disabled={disabled}
                            onClick={() => setSampleDialog({ mode: 'edit', index: idx })}
                            data-testid={`quotation-form-sample-edit-${idx}`}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Hapus">
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            aria-label={`Hapus sampel baris ${idx + 1}`}
                            disabled={disabled}
                            onClick={() => onSampleRowsChange(sampleRows.filter((_, i) => i !== idx))}
                            data-testid={`quotation-form-sample-delete-${idx}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <QuotationSampleFormDialog
        open={sampleDialog != null}
        onClose={() => setSampleDialog(null)}
        initialRow={sampleDialog?.mode === 'edit' ? sampleDialogInitial : null}
        onSave={(row) => {
          if (sampleDialog?.mode === 'create') {
            onSampleRowsChange([...sampleRows, row]);
          } else if (sampleDialog?.mode === 'edit') {
            const i = sampleDialog.index;
            onSampleRowsChange(sampleRows.map((r, j) => (j === i ? row : r)));
          }
        }}
      />

      <Divider sx={{ my: 2 }} />

      <QuotationFormBiayaLainnyaTabScreen
        disabled={disabled}
        readOnly={readOnly}
        sampleRows={rowsForFinancial}
        sampleRecurringCounts={financialSampleRecurringCounts}
        groupSampleSubtotals={groupSampleSubtotals}
        groupOtherCostSubtotals={groupOtherCostSubtotals}
        allOtherCostRowsForGrandTotal={allOtherCostRowsForGrandTotal}
        allOtherCostRecurringCounts={allOtherCostRecurringCounts}
        otherCostRecurringCount={contractRecurringCount}
        discount_amount={discount_amount}
        discount_is_percentage={discount_is_percentage}
        discount_percentage={discount_percentage}
        is_ppn={is_ppn}
        otherCostRows={otherCostRows}
        onPatch={onFinancialPatch}
        onNewOtherCost={() => setCostDialog({ mode: 'create' })}
        onEditOtherCost={(index) => setCostDialog({ mode: 'edit', index })}
        onDeleteOtherCost={(index) => {
          const next = otherCostRows.filter((_, i) => i !== index);
          onFinancialPatch({ otherCostRows: next });
        }}
        showGrandTotalSummary={!hideFinancialSummary}
      />

      <QuotationOtherCostFormDialog
        open={costDialog != null}
        onClose={() => setCostDialog(null)}
        initialRow={costDialog?.mode === 'edit' ? costDialogInitial : null}
        onSave={(row) => {
          if (costDialog?.mode === 'create') {
            onFinancialPatch({ otherCostRows: [...otherCostRows, row] });
          } else if (costDialog?.mode === 'edit') {
            const i = costDialog.index;
            onFinancialPatch({ otherCostRows: otherCostRows.map((r, j) => (j === i ? row : r)) });
          }
        }}
      />
    </Stack>
  );
}
